/*
  Variant text tokens, usable in any text setting or metaobject text sitewide:
    [Size_Variant_Name]          the selected Size option value + suffix ("3L Magnum Smoke Generator")
    [Power_Source_Variant_Name]  the selected Power Source option value
    [Upsell_Variant_Name]        each ticked upsell in .complete_rig-wrapper, one bullet per upsell

  layout/theme.liquid swaps each token for <span data-sm-token="…"> holding the initial value (see
  snippets/sm-variant-tokens.liquid). The product configurators (sm-reusable-product_product,
  sm-main-product-navigation.js) call window.smPublishVariantTokens on every variant/upsell change;
  this script then rewrites every token on the page. Tokens in markup added later (Section Rendering
  API re-renders, theme editor) are converted by the MutationObserver below.
*/
(() => {
  if (window.smVariantTokensReady) return;
  window.smVariantTokensReady = true;

  const TOKENS = {
    '[Size_Variant_Name]': 'size',
    '[Power_Source_Variant_Name]': 'power',
    '[Upsell_Variant_Name]': 'upsells',
  };
  /* Suffix and fallbacks come from Theme settings > Tokens Behaviour, printed into <head> by
     snippets/sm-variant-tokens.liquid. Fallbacks show when there's no Size / Power Source value. */
  const config = window.smVariantTokenConfig || {};
  const sizeText = (value) => {
    if (!value) return config.sizeFallback || '';
    return config.suffix ? `${value} ${config.suffix}` : value;
  };
  const powerText = (value) => value || config.powerFallback || '';
  const TOKEN_PATTERN = /\[(Size_Variant_Name|Power_Source_Variant_Name|Upsell_Variant_Name)\]/;

  /* Configurators that ran before this deferred script hit the stub in snippets/sm-variant-tokens.liquid,
     which keeps the latest call; it is replayed below once the real function exists. */
  const pending = window.smVariantTokenArgs;

  /* Called by the configurators. Reads option positions from the Size / Power Source blocks,
     falling back to option names containing "size" / "power". */
  window.smPublishVariantTokens = (root, productData, variant, checkboxes) => {
    if (!root || !productData || !variant) return;
    const names = productData.options || [];
    const optionValue = (selector, pattern) => {
      const group = root.querySelector(selector);
      let index = group ? Number(group.dataset.optionPosition) - 1 : -1;
      if (!(index >= 0)) index = names.findIndex((name) => pattern.test(String(name && name.name ? name.name : name)));
      return index >= 0 && variant.options ? variant.options[index] || '' : '';
    };
    const upsells = (checkboxes || []).map((checkbox) => {
      const card = checkbox.closest('[data-sm-upsell-card]');
      const title = (card && card.dataset.smUpsellTitle) || '';
      const select = card && card.querySelector('[data-sm-upsell-variant]');
      const option = select && select.selectedOptions[0];
      const variantTitle = option ? option.textContent.trim().split(' — ')[0].trim() : '';
      return variantTitle && variantTitle !== 'Default Title' ? `${title} – ${variantTitle}` : title;
    }).filter(Boolean);

    window.smVariantTokensFromConfigurator = true;
    window.smVariantTokenState = {
      productId: productData.id,
      size: sizeText(optionValue('[data-sm-smoker-size]', /size/i)),
      power: powerText(optionValue('[data-sm-power-source]', /power/i)),
      upsells: Array.from(new Set(upsells)),
    };
    document.dispatchEvent(new CustomEvent('sm:variant-tokens', { detail: window.smVariantTokenState }));
  };

  /* An upsell token that is the only content of a list item (rich-text bullets) renders each
     upsell as its own sibling <li>; the host <li> stays hidden (CSS :has in the snippet), so no
     empty bullet shows when nothing is ticked. */
  let hostCount = 0;
  const soleListItem = (el) => {
    const li = el.parentElement;
    if (!li || li.tagName !== 'LI') return null;
    const alone = Array.from(li.childNodes).every((n) => n === el || (n.nodeType === 3 && !n.nodeValue.trim()) || n.nodeName === 'BR');
    return alone ? li : null;
  };

  const render = (el, state) => {
    if (!state) return;
    const key = el.dataset.smToken;
    if (key === 'upsells') {
      const items = state.upsells || [];
      const host = soleListItem(el);
      if (host) {
        if (!el.dataset.smTokenId) el.dataset.smTokenId = `sm-upsells-${(hostCount += 1)}`;
        const id = el.dataset.smTokenId;
        host.parentElement.querySelectorAll(`:scope > [data-sm-token-for="${id}"]`).forEach((li) => li.remove());
        let after = host;
        items.forEach((name) => {
          const li = document.createElement('li');
          li.dataset.smTokenFor = id;
          li.textContent = name;
          after.after(li);
          after = li;
        });
        el.replaceChildren();
        el.hidden = true;
        return;
      }
      el.replaceChildren(...items.map((name) => {
        const item = document.createElement('span');
        item.className = 'sm-token__item';
        item.textContent = name;
        return item;
      }));
      el.hidden = !items.length;
    } else if (typeof state[key] === 'string') {
      el.textContent = state[key];
    }
  };

  const applyAll = (scope) => {
    const state = window.smVariantTokenState;
    if (!state) return;
    (scope || document).querySelectorAll('[data-sm-token]').forEach((el) => render(el, state));
  };

  /* Raw tokens in text nodes (markup that skipped the layout swap) become token spans. */
  const convert = (node) => {
    if (node.nodeType === 1 && /^(SCRIPT|STYLE|TEXTAREA)$/.test(node.tagName)) return;
    if (!TOKEN_PATTERN.test(node.textContent || '')) return;
    const texts = [];
    if (node.nodeType === 3) texts.push(node);
    else {
      const walker = document.createTreeWalker(node, NodeFilter.SHOW_TEXT);
      for (let t = walker.nextNode(); t; t = walker.nextNode()) {
        if (TOKEN_PATTERN.test(t.nodeValue) && !/^(SCRIPT|STYLE|TEXTAREA)$/.test(t.parentNode.tagName)) texts.push(t);
      }
    }
    texts.forEach((text) => {
      const parts = text.nodeValue.split(/(\[(?:Size_Variant_Name|Power_Source_Variant_Name|Upsell_Variant_Name)\])/);
      const fragment = document.createDocumentFragment();
      parts.forEach((part) => {
        if (TOKENS[part]) {
          const span = document.createElement('span');
          span.className = TOKENS[part] === 'upsells' ? 'sm-token sm-token--list' : 'sm-token';
          span.dataset.smToken = TOKENS[part];
          render(span, window.smVariantTokenState);
          fragment.appendChild(span);
        } else if (part) {
          fragment.appendChild(document.createTextNode(part));
        }
      });
      text.parentNode.replaceChild(fragment, text);
    });
  };

  document.addEventListener('sm:variant-tokens', () => applyAll());
  if (pending) window.smPublishVariantTokens(...pending);

  /* Standard product pages (main-product): size / power follow the theme's variant-change event,
     using the option names seeded in <head>. Skipped where a configurator publishes, so its
     upsell list isn't wiped. */
  const subscribeToVariantChange = () => {
    /* PUB_SUB_EVENTS is a top-level const (constants.js), not a window property. */
    if (typeof subscribe !== 'function' || typeof PUB_SUB_EVENTS === 'undefined') return;
    subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
      const variant = event && event.data && event.data.variant;
      if (!variant || window.smVariantTokensFromConfigurator) return;
      const names = window.smVariantTokenOptions || [];
      const valueFor = (pattern) => {
        const index = names.findIndex((name) => pattern.test(String(name && name.name ? name.name : name)));
        return index >= 0 && variant.options ? variant.options[index] || '' : '';
      };
      const sizeValue = valueFor(/size/i);
      const previous = window.smVariantTokenState || {};
      window.smVariantTokenState = {
        productId: previous.productId,
        size: sizeText(sizeValue),
        power: powerText(valueFor(/power/i)),
        upsells: previous.upsells || [],
      };
      document.dispatchEvent(new CustomEvent('sm:variant-tokens', { detail: window.smVariantTokenState }));
    });
  };

  const start = () => {
    subscribeToVariantChange();
    convert(document.body);
    applyAll();
    new MutationObserver((mutations) => {
      mutations.forEach((mutation) => mutation.addedNodes.forEach((node) => {
        convert(node);
        if (node.nodeType === 1 && window.smVariantTokenState) {
          if (node.matches('[data-sm-token]')) render(node, window.smVariantTokenState);
          applyAll(node);
        }
      }));
    }).observe(document.body, { childList: true, subtree: true });
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start);
  else start();
})();
